// Attribution payload captured from the LP visit (URL params + persistent IDs).
// Persisted to sessionStorage by `useLpAnalytics` on the first paid/UTM-tagged
// landing, then read by reservation form submit handlers and forwarded to the
// API so it can be stored on counseling_reservations / trial_lesson_reservations.

export const LP_ATTRIBUTION_KEY = "lp_attribution";

export type LpAttribution = {
  ad_creative: string | null;
  source: string | null;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  fbclid: string | null;
  landing_page: string | null;
  visitor_id: string | null;
};

const EMPTY: LpAttribution = {
  ad_creative: null,
  source: null,
  utm_source: null,
  utm_medium: null,
  utm_campaign: null,
  fbclid: null,
  landing_page: null,
  visitor_id: null,
};

/**
 * Read the LP attribution snapshot stored by `useLpAnalytics`.
 * Returns an all-null payload on SSR, when sessionStorage is unavailable,
 * or when no attribution has been captured yet (e.g. direct visits).
 */
export function readLpAttribution(): LpAttribution {
  if (typeof window === "undefined") return { ...EMPTY };
  try {
    const raw = window.sessionStorage.getItem(LP_ATTRIBUTION_KEY);
    if (!raw) return { ...EMPTY };
    const parsed = JSON.parse(raw) as Partial<LpAttribution>;
    return {
      ad_creative: parsed.ad_creative ?? null,
      source: parsed.source ?? null,
      utm_source: parsed.utm_source ?? null,
      utm_medium: parsed.utm_medium ?? null,
      utm_campaign: parsed.utm_campaign ?? null,
      fbclid: parsed.fbclid ?? null,
      landing_page: parsed.landing_page ?? null,
      visitor_id: parsed.visitor_id ?? null,
    };
  } catch {
    return { ...EMPTY };
  }
}

/**
 * Persist the LP attribution snapshot. Only writes when the snapshot has at
 * least one trackable signal AND no prior snapshot exists — this prevents a
 * later organic visit from overwriting earlier paid-source attribution within
 * the same browser session.
 */
// 最後に来た広告の印と時刻。lp_attribution (最初の印、予約フォーム用) とは別に置き、
// HP の訪問 (apps/women-astro/src/client/hp-visit.ts) が新しいほうの印を引き継ぐのに使う
export const LP_LAST_ATTRIBUTION_KEY = "lp_last_attribution";

export function writeLpAttributionIfNew(attribution: LpAttribution): void {
  if (typeof window === "undefined") return;
  try {
    const hasSignal =
      attribution.ad_creative ||
      attribution.fbclid ||
      attribution.utm_source ||
      attribution.utm_medium ||
      attribution.utm_campaign;
    if (!hasSignal) return;
    window.sessionStorage.setItem(LP_LAST_ATTRIBUTION_KEY, JSON.stringify({ ...attribution, at: Date.now() }));
    const existing = window.sessionStorage.getItem(LP_ATTRIBUTION_KEY);
    if (existing) return;
    window.sessionStorage.setItem(LP_ATTRIBUTION_KEY, JSON.stringify(attribution));
  } catch {
    // Storage may be disabled (private mode, quota) — silently ignore.
  }
}
